import StandardBlockModel from 'Models/Blocks/StandardBlock/StandardBlockModel.interface';
import XHtmlArea from 'Molecules/XHtmlArea/XHtmlArea';
import { applyEditModeAttr, canUseDOM } from 'Shared/Common/Helpers';
import useMedia from 'Shared/Hooks/useMedia';
import { styled } from 'stitches.config';
import { mediaQueryTypes } from 'Theme/Settings/mediaQueries';
import { baseHeadingStyle } from 'Atoms/Typography/Headings/BaseHeadingStyle';
import Image from 'Atoms/Image/Image';
import { useEffect, useMemo, useRef, useState } from 'react';
import IconLink from 'Atoms/Links/IconLink';
import { SizeConstants } from 'Theme/Settings/sizes';
import GridItem from 'Atoms/Grids/GridItem';

type PropTypes = {
  content: StandardBlockModel;
};

function StandardBlockDefault({
  content: {
    heading,
    image,
    rightPositioned,
    bodyText,
    inEditMode,
    links,
    subheading,
  },
}: PropTypes) {
  const windowDim = useWindowDimensions();
  const isMobile = useMedia(mediaQueryTypes.mediaMaxLarge);
  const lazyLoadRef = useRef<HTMLDivElement>(null);

  const calcMargin = useMemo(() => {
    if (canUseDOM()) {
      if (windowDim.innerWidth > 1328) {
        const customWidth =
          (windowDim.innerWidth - SizeConstants.contentMaxWidth) / 2;

        return rightPositioned
          ? {
              marginLeft: customWidth + 'px',
            }
          : {
              marginRight: customWidth + 'px',
            };
      }
    }
  }, [windowDim, rightPositioned]);

  return (
    <GridItem layout="FullWidth">
      <Root ref={lazyLoadRef}>
        <CC>
          <Column
            css={{
              ...calcMargin,
            }}
            rightPositioned={rightPositioned}
          >
            {image && (
              <ImageArea
                css={{
                  order: isMobile || !rightPositioned ? '0' : '1',
                }}
                {...applyEditModeAttr(inEditMode && 'Image')}
              >
                <Image
                  src={image.src + `?preset=standard-block-left-main`}
                  alt={image.alt}
                  externalRef={lazyLoadRef}
                />
              </ImageArea>
            )}

            <TextContainer
              css={{
                justifyContent: isMobile
                  ? 'flex-start'
                  : rightPositioned
                  ? 'flex-start'
                  : 'flex-end',
              }}
              rightPositioned={rightPositioned}
            >
              <TextWrapper>
                <SubHeadingWrapper>{subheading} </SubHeadingWrapper>

                <Heading {...applyEditModeAttr(inEditMode && 'Heading')}>
                  {heading.toUpperCase()}
                </Heading>
                <BodyWrapper>
                  <XHtmlArea
                    css={{ lineHeight: '$lh15' }}
                    {...applyEditModeAttr(inEditMode && 'BodyText')}
                    content={bodyText}
                  />
                </BodyWrapper>
                {links &&
                  links.map((item, index) => (
                    <IconLink
                      type="Arrow"
                      href={item.href}
                      key={item.id + index}
                    >
                      {item.text}
                    </IconLink>
                  ))}
              </TextWrapper>
            </TextContainer>
          </Column>
        </CC>
      </Root>
    </GridItem>
  );
}

function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState<any>({
    innerWidth: 0,
    innerHeight: 0,
  });

  useEffect(() => {
    canUseDOM() &&
      setWindowDimensions({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
      });
    function handleResize() {
      if (canUseDOM())
        setWindowDimensions({
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
        });
    }
    if (canUseDOM()) window.addEventListener('resize', handleResize);

    if (canUseDOM())
      return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

export default StandardBlockDefault;
const CC = styled('div', {
  w: '100%',
  mx: 'auto',
  py: 0,
});

const Root = styled('div', {
  h: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  mb: 4,
  '@mediaMinLarge': {
    mb: 30,
  },
  '&:nth-child(n)[data-blocklayout="1/1"]': {
    '@mediaMinMedium': {
      flexWrap: 'nowrap',
      mb: 30,
    },
  },
});

const SubHeadingWrapper = styled('div', {
  mb: 2,
  fs: 6,
  color: '$primaryLight3',
  lineHeight: '$lh133',
});

const BodyWrapper = styled('div', {
  mb: 8,
});

const Column = styled('div', {
  display: 'grid',
  g: 8,
  marginLeft: 'auto',
  width: 'auto',
  variants: {
    rightPositioned: {
      true: {
        '@mediaMinLarge': {
          gridTemplateColumns: '25% 73%',
          pl: 5,
        },
      },
      false: {
        '@mediaMinLarge': {
          gridTemplateColumns: '53% 45%',
          pr: 5,
        },
      },
    },
  },
});

const ImageArea = styled('section', {
  w: '100%',
  display: 'flex',
  position: 'relative',
  order: 0,
  '& img': {
    height: 'inherit',
    width: 'inherit',
    objectFit: 'cover',
    aspectRatio: '16/9',
  },

  height: '238px',
  '@mediaMinMedium': {
    height: '442px',
  },
  '@mediaMinLarge': {
    height: '468px',
  },
  '@mediaMinMassive': {
    height: 'auto',
  },
});

const TextContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  maxWidth: '590px',
  pt: 5,
  w: '100%',
  color: '$textPrimary',
  p: 4,
  '@mediaMinLarge': {
    p: 0,
  },
  variants: {
    rightPositioned: {
      false: {
        '@mediaMinLarge': {
          pl: 18,
        },
      },
    },
  },
});

const TextWrapper = styled('div', {
  wordBreak: 'break-word',
  '@mediaMinLarge': {
    width: '416px',
  },
  width: 'auto',
});

const Heading = styled('h2', {
  ...baseHeadingStyle,
  fs: 12,
  lineHeight: '$lh116',
  fontFamily: '$fontPrimary400',
  mt: 0,
  mb: 4,
});
